import React from 'react';
import classNames from 'classnames';
import { action } from 'mobx';
import { observer } from 'mobx-react';
import { toast } from 'react-hot-toast';
import { useHistory } from 'react-router-dom';

import { useStyles } from 'ui/base/theme';
import { ButtonClickEvent } from 'ui/base/types';
import { Link } from 'ui/base/link';
import { BusyStore } from 'ui/base/button';
import { ChoiceStore } from 'ui/base/input';
import { Modal, ModalStore } from 'ui/base/modal';
import { ToastID, defaultToast } from 'ui/base/toast';
import { StandardPage } from 'ui/base/page/standard_page';

import { RadioInput } from 'ui/standard/input/radio_input';

import { ActiveButton } from 'ui/public/button';

import { createAddressFilter } from 'ui/history/site_entry/site_entry_logic';

import { Optional, ResultKind } from 'services/base/types';
import { BasePath, pagePathToPathString } from 'services/base/page_path';

import IssueMessages from 'services/pangolin/logic/issues/issue_malicious_address_messages';
import { IPangolinDomain, MaliciousAddressIssue, KnownIssue, HistoryFilter } from 'services/pangolin/types';

import { HostDeviceType } from 'services/pangolin/protos/hosts_pb';
import { hostDisplayName, hostDisplayDeviceType } from 'services/pangolin/hosts_service';

import { IssuesService } from 'services/pangolin/issues_service';

import {
  ActionIconStyles,
  themedActionIconImageStyles,
} from 'assets/enums/actions/actions';

import {
  themedModuleIconImageStyles,
} from 'assets/enums/modules/modules';

import {
  themedDeviceTypeIconImageStyles,
  getDeviceTypeIcon,
} from 'assets/enums/device_types/device_types';

import {
  ViewIssuePartialStyles,
  viewIssuePartialThemedStyles,

  themedFieldIconImageStyles,
} from './partial_styles';

import Messages from './view_issue_malicious_address_messages';

function getActionName(action: string): Optional<string> {
  return action === 'allow' ? IssueMessages.actionValueAllow() :
         action === 'block' ? IssueMessages.actionValueBlock() :
         undefined;
}

function getActionIcon(action: string, actionIconImages: ActionIconStyles): Optional<string> {
  return action === 'allow' ? actionIconImages.allowed :
         action === 'block' ? actionIconImages.blocked :
         undefined;
}

function getActionLabel(action: string): Optional<string> {
  return action === 'allow' ? IssueMessages.actionLabelAllow() :
         action === 'block' ? IssueMessages.actionLabelBlock() :
         undefined;
}

type PeerModalProps = {
  styles: ViewIssuePartialStyles;
  domain: IPangolinDomain;
  issue: MaliciousAddressIssue;
  store: ModalStore;
};

export const PeerModal = observer(({ styles, domain, issue, store }: PeerModalProps) => {
  const history = useHistory();

  const fieldIconImages = useStyles(themedFieldIconImageStyles);
  const moduleIconImages = useStyles(themedModuleIconImageStyles);

  const peer_name = issue.data.address;

  const incident_last = (issue as KnownIssue).incidents.getAt(0);

  const filter_object = incident_last ? (() => {
    const filter_last = new Date(incident_last.timestamp);
    filter_last.setMinutes(filter_last.getMinutes() + 1);

    const filter_first = new Date(incident_last.timestamp);
    filter_first.setMinutes(filter_first.getMinutes() - 1);

    return { end: filter_last, start: filter_first,
             sites: [createAddressFilter(peer_name)] };
  })() : undefined;

  const url_abuseipdb = `https://www.abuseipdb.com/check/${peer_name}`;
  const url_alienvault = `https://otx.alienvault.com/indicator/ip/${peer_name}`;
  const url_cloudflare = `https://radar.cloudflare.com/ip/${peer_name}`;
  const url_urlscanio = `https://urlscan.io/ip/${peer_name}`;
  const url_virustotal = `https://www.virustotal.com/gui/ip-address/${peer_name}`;

  const onModalClose = React.useCallback((event: ButtonClickEvent) => {
    event.preventDefault();
    store.hideModal();
  }, [store]);

  const onViewHistory = React.useCallback((event: ButtonClickEvent, filter: Partial<HistoryFilter>) => {
    event.preventDefault();
    domain.historyFilter = {
      ...domain.historyFilter,
      ...filter,
    };
    domain.historyFiltered = true;
    history.push(pagePathToPathString({
      kind: BasePath.HISTORY_LIST,
      pangolinID: domain.id,
    }));
  }, [domain, history]);

  return (
    <Modal store={store}>
      <div className={classNames(styles.modalContainer, { [styles.modalContainerPopOut]: store.visible })}>
        <StandardPage>
          <div className={styles.modalHeaderRow}>
            {Messages.modalPeerTitle()}
            <button
              className={styles.modalCloseIcon}
              onClick={onModalClose}
              type='button'
            />
          </div>
          <div className={styles.modalContentRow}>
            {
              filter_object ? (
                <button className={classNames(styles.field, styles.item, styles.clickable)}
                        type='button' onClick={(event) => onViewHistory(event, filter_object)}>
                  <img className={styles.fieldIcon} src={moduleIconImages.history} alt='History Icon' />
                  <div className={styles.fieldTextRight}>
                    {Messages.modalPeerLinkHistoryTitle()}
                    <div className={styles.fieldInfoLong}>
                      {Messages.modalPeerLinkHistoryLabel()}
                    </div>
                  </div>
                  <div className={styles.open}/>
                </button>
              ) : (
                <div className={classNames(styles.field, styles.item, styles.disabled)}>
                  <img className={styles.fieldIcon} src={moduleIconImages.history} alt='History Icon' />
                  <div className={styles.fieldTextRight}>
                    {Messages.modalPeerLinkHistoryTitle()}
                    <div className={styles.fieldInfoLong}>
                      {Messages.modalPeerLinkHistoryLabel()}
                    </div>
                  </div>
                  <div className={styles.open}/>
                </div>
              )
            }
            <div className={classNames(styles.field)} />
            <a className={classNames(styles.field, styles.item, styles.clickable)}
               target='_blank' rel='noopener noreferrer' href={url_abuseipdb}>
              <img className={styles.fieldIcon} src={fieldIconImages.browser} alt='Browser Icon' />
              <div className={styles.fieldTextRight}>
                {Messages.modalPeerLinkAbuseIPDBTitle()}
                <div className={styles.fieldInfoLong}>
                  {Messages.modalPeerLinkAbuseIPDBLabel()}
                </div>
              </div>
              <div className={styles.open}/>
            </a>
            <a className={classNames(styles.field, styles.item, styles.clickable)}
               target='_blank' rel='noopener noreferrer' href={url_alienvault}>
              <img className={styles.fieldIcon} src={fieldIconImages.browser} alt='Browser Icon' />
              <div className={styles.fieldTextRight}>
                {Messages.modalPeerLinkAlienVaultTitle()}
                <div className={styles.fieldInfoLong}>
                  {Messages.modalPeerLinkAlienVaultLabel()}
                </div>
              </div>
              <div className={styles.open}/>
            </a>
            <a className={classNames(styles.field, styles.item, styles.clickable)}
               target='_blank' rel='noopener noreferrer' href={url_cloudflare}>
              <img className={styles.fieldIcon} src={fieldIconImages.browser} alt='Browser Icon' />
              <div className={styles.fieldTextRight}>
                {Messages.modalPeerLinkCloudflareTitle()}
                <div className={styles.fieldInfoLong}>
                  {Messages.modalPeerLinkCloudflareLabel()}
                </div>
              </div>
              <div className={styles.open}/>
            </a>
            <a className={classNames(styles.field, styles.item, styles.clickable)}
               target='_blank' rel='noopener noreferrer' href={url_urlscanio}>
              <img className={styles.fieldIcon} src={fieldIconImages.browser} alt='Browser Icon' />
              <div className={styles.fieldTextRight}>
                {Messages.modalPeerLinkURLScanIOTitle()}
                <div className={styles.fieldInfoLong}>
                  {Messages.modalPeerLinkURLScanIOLabel()}
                </div>
              </div>
              <div className={styles.open}/>
            </a>
            <a className={classNames(styles.field, styles.item, styles.clickable)}
               target='_blank' rel='noopener noreferrer' href={url_virustotal}>
              <img className={styles.fieldIcon} src={fieldIconImages.browser} alt='Browser Icon' />
              <div className={styles.fieldTextRight}>
                {Messages.modalPeerLinkVirusTotalTitle()}
                <div className={styles.fieldInfoLong}>
                  {Messages.modalPeerLinkVirusTotalLabel()}
                </div>
              </div>
              <div className={styles.open}/>
            </a>
          </div>
        </StandardPage>
      </div>
    </Modal>
  );
});

type HostModalProps = {
  styles: ViewIssuePartialStyles;
  domain: IPangolinDomain;
  issue: MaliciousAddressIssue;
  store: ModalStore;
};

export const HostModal = observer(({ styles, domain, issue, store }: HostModalProps) => {
  const history = useHistory();

  const moduleIconImages = useStyles(themedModuleIconImageStyles);

  const host_host = domain.hosts.getMac(issue.data.mac);

  const incident_last = (issue as KnownIssue).incidents.getAt(0);

  const filter_object = incident_last && host_host ? (() => {
    const filter_last = new Date(incident_last.timestamp);
    filter_last.setMinutes(filter_last.getMinutes() + 1);

    const filter_first = new Date(incident_last.timestamp);
    filter_first.setMinutes(filter_first.getMinutes() - 1);

    return { end: filter_last, start: filter_first,
             devices: [host_host.id] };
  })() : undefined;

  const onModalClose = React.useCallback((event: ButtonClickEvent) => {
    event.preventDefault();
    store.hideModal();
  }, [store]);

  const onViewHistory = React.useCallback((event: ButtonClickEvent, filter: Partial<HistoryFilter>) => {
    event.preventDefault();
    domain.historyFilter = {
      ...domain.historyFilter,
      ...filter,
    };
    domain.historyFiltered = true;
    history.push(pagePathToPathString({
      kind: BasePath.HISTORY_LIST,
      pangolinID: domain.id,
    }));
  }, [domain, history]);

  return (
    <Modal store={store}>
      <div className={classNames(styles.modalContainer, { [styles.modalContainerPopOut]: store.visible })}>
        <StandardPage>
          <div className={styles.modalHeaderRow}>
            {Messages.modalHostTitle()}
            <button
              className={styles.modalCloseIcon}
              onClick={onModalClose}
              type='button'
            />
          </div>
          <div className={styles.modalContentRow}>
            {
              host_host ? (
                <Link className={classNames(styles.field, styles.item, styles.clickable)}
                      path={{ kind: BasePath.DEVICES_VIEW, pangolinID: domain.id,
                              deviceID: host_host.id }}>
                  <img className={styles.fieldIcon} src={moduleIconImages.devices} alt='Device Icon' />
                  <div className={styles.fieldTextRight}>
                    {Messages.modalHostLinkDeviceTitle()}
                    <div className={styles.fieldInfoLong}>
                      {Messages.modalHostLinkDeviceLabel()}
                    </div>
                  </div>
                  <div className={styles.open}/>
                </Link>
              ) : (
                <div className={classNames(styles.field, styles.item, styles.disabled)}>
                  <img className={styles.fieldIcon} src={moduleIconImages.devices} alt='Device Icon' />
                  <div className={styles.fieldTextRight}>
                    {Messages.modalHostLinkDeviceTitle()}
                    <div className={styles.fieldInfoLong}>
                      {Messages.modalHostLinkDeviceLabel()}
                    </div>
                  </div>
                  <div className={styles.open}/>
                </div>
              )
            }
            {
              filter_object ? (
                <button className={classNames(styles.field, styles.item, styles.clickable)}
                        type='button' onClick={(event) => onViewHistory(event, filter_object)}>
                  <img className={styles.fieldIcon} src={moduleIconImages.history} alt='History Icon' />
                  <div className={styles.fieldTextRight}>
                    {Messages.modalHostLinkTrafficTitle()}
                    <div className={styles.fieldInfoLong}>
                      {Messages.modalHostLinkTrafficLabel()}
                    </div>
                  </div>
                  <div className={styles.open}/>
                </button>
              ) : (
                <div className={classNames(styles.field, styles.item, styles.disabled)}>
                  <img className={styles.fieldIcon} src={moduleIconImages.history} alt='History Icon' />
                  <div className={styles.fieldTextRight}>
                    {Messages.modalHostLinkTrafficTitle()}
                    <div className={styles.fieldInfoLong}>
                      {Messages.modalHostLinkTrafficLabel()}
                    </div>
                  </div>
                  <div className={styles.open}/>
                </div>
              )
            }
          </div>
        </StandardPage>
      </div>
    </Modal>
  );
});

type ActionInputRowProps = {
  styles: ViewIssuePartialStyles;
  store: ChoiceStore<string>;
  choice: string;
};

const ActionInputRow = observer(({ styles, store, choice }: ActionInputRowProps) => {
  const actionIconImages = useStyles(themedActionIconImageStyles);

  const action_name = getActionName(choice);
  const action_label = getActionLabel(choice);
  const action_icon = getActionIcon(choice, actionIconImages);

  const selected = choice === store.selected;

  const choice_store = React.useCallback(action(() => {
    store.setSelected(choice);
  }), [store]);

  return (
    <div
      className={classNames(styles.field, styles.clickable,
                            selected ? styles.cardSelected :
                                       styles.cardDefault)}
      onClick={choice_store}
    >
      <img className={styles.cardIcon} src={action_icon} alt='Action Icon' />
      <div className={styles.cardText}>
        {action_name}
        <div className={styles.cardInfo}>
          {action_label}
        </div>
      </div>
      <RadioInput
        store={store}
        choice={choice}
      />
    </div>
  );
});

type ActionModalProps = {
  styles: ViewIssuePartialStyles;
  domain: IPangolinDomain;
  issue: MaliciousAddressIssue;
  store: ModalStore;
};

export const ActionModal = observer(({ styles, domain, issue, store }: ActionModalProps) => {
  const action_type = JSON.parse((issue as KnownIssue).action);
  const action_choices: Record<string, string> = {
    allow: JSON.stringify('allow'),
    block: JSON.stringify('block'),
  };
  const [action_store] = React.useState(
    new ChoiceStore<string>(action_choices, action_type)
  );

  React.useEffect(() => {
    action_store.setSelected(action_type);
  }, [store.visible, action_store, action_type]);

  const [busy_store] = React.useState(new BusyStore());

  const onModalClose = React.useCallback((event: ButtonClickEvent) => {
    event.preventDefault();
    store.hideModal();
  }, [store]);

  const onUpdateAction = React.useCallback(async() => {
    store.freezeModal();
    await busy_store.perform(async() => {
      const result = await IssuesService.update(
        domain, {
          fpid: (issue as KnownIssue).fpid,
          action: action_store.value,
        }
      );
      store.unfreezeModal();
      if (result.kind === ResultKind.OK) {
        toast.dismiss(ToastID.ERROR_ACTION_PANGOLIN_OFFLINE);
        toast.dismiss(ToastID.ERROR_ACTION_PLAN_REQUIRED);
        toast.dismiss(ToastID.ERROR_ACTION_GENERIC);
        store.hideModal();
      } else if (result.kind === ResultKind.USER_ERROR && result.status === 402) {
        defaultToast(ToastID.ERROR_ACTION_PLAN_REQUIRED);
      } else if (result.kind === ResultKind.SYSTEM_ERROR && result.status === 502) {
        defaultToast(ToastID.ERROR_ACTION_PANGOLIN_OFFLINE);
      } else {
        defaultToast(ToastID.ERROR_ACTION_GENERIC);
      }
    });
  }, [domain, issue, store, action_store, busy_store]);

  return (
    <Modal store={store}>
      <div className={classNames(styles.modalContainer, { [styles.modalContainerPopOut]: store.visible })}>
        <StandardPage>
          <div className={styles.modalHeaderRow}>
            {Messages.modalActionTitle()}
            <button
              className={styles.modalCloseIcon}
              onClick={onModalClose}
              type='button'
            />
          </div>
          <div className={styles.modalContentRow}>
            <ActionInputRow
              choice='block'
              styles={styles}
              store={action_store}
            />
            <ActionInputRow
              choice='allow'
              styles={styles}
              store={action_store}
            />
            <div className={styles.spacer}>
              <ActiveButton busy={busy_store} onClick={onUpdateAction}>
                {Messages.modalActionButtonOK()}
              </ActiveButton>
            </div>
          </div>
        </StandardPage>
      </div>
    </Modal>
  );
});

type MaliciousAddressViewIssueSummaryPartialProps = {
  domain: IPangolinDomain;
  issue: MaliciousAddressIssue;
};
export const MaliciousAddressViewIssueSummaryPartial = observer(({ domain, issue }: MaliciousAddressViewIssueSummaryPartialProps) => {
  const styles: ViewIssuePartialStyles = useStyles(viewIssuePartialThemedStyles);

  const fieldIconImages = useStyles(themedFieldIconImageStyles);
  const actionIconImages = useStyles(themedActionIconImageStyles);
  const deviceIconImages = useStyles(themedDeviceTypeIconImageStyles);

  const peer_name = issue.data.address;
  const peer_icon = fieldIconImages.address;

  const host_host = domain.hosts.getMac(issue.data.mac);
  const host_name = host_host ? (hostDisplayName(host_host) ?? host_host.mac) : issue.data.mac;
  const host_type = host_host ? hostDisplayDeviceType(host_host) : HostDeviceType.H_UNKNOWN;
  const host_icon = getDeviceTypeIcon(deviceIconImages, host_type);

  const action_type = JSON.parse((issue as KnownIssue).action);
  const action_name = getActionName(action_type);
  const action_icon = getActionIcon(action_type, actionIconImages);

  const [peer_show] = React.useState(new ModalStore(false));
  React.useEffect(peer_show.hideOnBackEffect);
  const [host_show] = React.useState(new ModalStore(false));
  React.useEffect(host_show.hideOnBackEffect);
  const [action_show] = React.useState(new ModalStore(false));
  React.useEffect(action_show.hideOnBackEffect);

  const onShowPeerModal = React.useCallback(action(() => {
    peer_show.showModal();
  }), [peer_show]);

  const onShowHostModal = React.useCallback(action(() => {
    host_show.showModal();
  }), [host_show]);

  const onShowActionModal = React.useCallback(action(() => {
    action_show.showModal();
  }), [action_show]);

  return (
    <>
      <PeerModal
        styles={styles}
        domain={domain}
        issue={issue}
        store={peer_show}
      />
      <HostModal
        styles={styles}
        domain={domain}
        issue={issue}
        store={host_show}
      />
      <ActionModal
        styles={styles}
        domain={domain}
        issue={issue}
        store={action_show}
      />
      <button className={classNames(styles.field, styles.clickable)}
              type='button' onClick={onShowPeerModal}>
        <div className={styles.fieldTextLeft}>
          {peer_name}
          <div className={styles.fieldInfo}>
            {Messages.fieldInfoAddress()}
          </div>
        </div>
        <img className={styles.fieldIcon} src={peer_icon} alt='Peer Icon' />
        <div className={styles.open}/>
      </button>
      <button className={classNames(styles.field, styles.clickable)}
              type='button' onClick={onShowHostModal}>
        <div className={styles.fieldTextLeft}>
          {host_name}
          <div className={styles.fieldInfo}>
            {Messages.fieldInfoHost()}
          </div>
        </div>
        <img className={styles.fieldIcon} src={host_icon} alt='Host Icon' />
        <div className={styles.open}/>
      </button>
      <button className={classNames(styles.field, styles.clickable)}
              type='button' onClick={onShowActionModal}>
        <div className={styles.fieldTextLeft}>
          {action_name}
          <div className={styles.fieldInfo}>
            {Messages.fieldInfoAction()}
          </div>
        </div>
        <img className={styles.fieldIcon} src={action_icon} alt='Action Icon' />
        <div className={styles.open}/>
      </button>
    </>
  );
});
